import { api } from './api';
import type { MeResponse } from '@shared/types';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient(config: {
            client_id: string;
            scope: string;
            ux_mode: 'popup';
            callback: (response: { code: string; error?: string }) => void;
          }): { requestCode(): void };
        };
      };
    };
  }
}

const GOOGLE_SCOPES = 'openid email profile';

/**
 * Google OAuth Client ID.
 * NENÍ to secret: Google ho posílá v URL při OAuth flow, je veřejně viditelný.
 * Pro LeadHub se teprve zakládá vlastní OAuth client (Google Cloud → Credentials),
 * proto je fallback prázdný — bez `VITE_GOOGLE_CLIENT_ID` přihlášení nefunguje.
 * Nastav ho v `frontend/.env.local` (dev) a v build env (CI/deploy):
 *   VITE_GOOGLE_CLIENT_ID=xxx.apps.googleusercontent.com
 * Musí odpovídat GOOGLE_CLIENT_ID secretu workeru.
 */
export const GOOGLE_CLIENT_ID =
  (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ||
  '45496654284-kf3n91rv8hnmsi1b2ek7ai59q26pcdc4.apps.googleusercontent.com';

export async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await api.get<MeResponse>('/me');
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

/**
 * Spustí Google Identity Services popup flow a vymění auth code u backendu za session cookie.
 * Vyžaduje `client_id` — předáváme ho z Vite env (VITE_GOOGLE_CLIENT_ID).
 */
export function signInWithGoogle(clientId: string): Promise<MeResponse['user']> {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services se nenačetly'));
      return;
    }

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: GOOGLE_SCOPES,
      ux_mode: 'popup',
      callback: async (response) => {
        if (response.error) {
          reject(new Error(response.error));
          return;
        }
        try {
          const result = await api.post<{ user: MeResponse['user'] }>('/auth/google/callback', {
            code: response.code,
            redirect_uri: 'postmessage',
          });
          resolve(result.user);
        } catch (err) {
          reject(err);
        }
      },
    });
    client.requestCode();
  });
}
